
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AVAILABLE_CHALLENGES } from "@/data/challenges";
import { ChallengeStatus, ChallengeProgress, ChallengeCounts } from "@/types/challenges";

export const useChallengeProgress = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: challengeStatuses, isLoading } = useQuery({
    queryKey: ['challenge-progress', user?.id],
    queryFn: async (): Promise<ChallengeStatus[]> => {
      if (!user?.id) return [];

      // Get all progress for the user
      const { data: progressData, error } = await supabase
        .from('user_challenge_progress')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching challenge progress:', error);
        return [];
      }

      // Get current counts using the edge function
      const { data: currentCounts, error: countsError } = await supabase.functions.invoke('get-challenge-counts', {
        body: { target_user_id: user.id }
      });

      if (countsError) {
        console.error('Error fetching challenge counts:', countsError);
      }

      // Ensure currentCounts has the right structure or use defaults
      const counts: ChallengeCounts = {
        timelineEntriesCount: currentCounts?.timelineEntriesCount || 0,
        invitationsSentCount: currentCounts?.invitationsSentCount || 0,
        friendEntriesReceivedCount: currentCounts?.friendEntriesReceivedCount || 0,
        entriesForFriendsCount: currentCounts?.entriesForFriendsCount || 0
      };

      // Map challenges with their progress
      return AVAILABLE_CHALLENGES.map(challenge => {
        const challengeProgress = progressData.filter(p => p.challenge_key === challenge.key);
        const completedTasks = challengeProgress.filter(p => p.is_completed).length;
        
        return {
          challenge,
          progress: challengeProgress.map(p => ({
            challengeKey: p.challenge_key,
            taskKey: p.task_key,
            isCompleted: p.is_completed,
            completedAt: p.completed_at || undefined
          })),
          completedTasks,
          totalTasks: challenge.tasks.length,
          isCompleted: completedTasks === challenge.tasks.length,
          currentCounts: counts
        };
      });
    },
    enabled: !!user?.id,
    // Increase staleTime to reduce unnecessary API calls
    staleTime: 60 * 1000, // 60 seconds - longer cache time to reduce CPU load
    // Reduce refetch frequency to help with CPU usage
    refetchOnWindowFocus: false, // Disable automatic refetch on window focus
    refetchInterval: 300 * 1000 // Only refetch every 5 minutes
  });

  // Simplified mutation since database triggers now handle most updates automatically
  const updateTaskProgressMutation = useMutation({
    mutationFn: async ({ challengeKey, taskKey, isCompleted }: { 
      challengeKey: string; 
      taskKey: string; 
      isCompleted: boolean; 
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_challenge_progress')
        .upsert({
          user_id: user.id,
          challenge_key: challengeKey,
          task_key: taskKey,
          is_completed: isCompleted,
          completed_at: isCompleted ? new Date().toISOString() : null
        });

      if (error) {
        // Log the error but don't throw for duplicate key violations
        if (error.message.includes('duplicate key value violates unique constraint')) {
          console.log('Task progress already exists:', { challengeKey, taskKey });
          return; // Silently ignore duplicate entries
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge-progress', user?.id] });
    },
    onError: (error) => {
      console.error('Error updating task progress:', error);
    }
  });

  // Manual trigger for progress update (mainly for edge cases)
  const updateChallengeProgressMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase.rpc('update_challenge_progress_for_user', {
        target_user_id: user.id
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge-progress', user?.id] });
    },
    onError: (error) => {
      console.error('Error updating challenge progress:', error);
    }
  });

  // Simplified progress calculation - database triggers now handle automatic completion
  const calculateTaskProgress = (challengeKey: string, taskKey: string, currentValue: number, target: number) => {
    // Check if we have challenge statuses and if the task is already completed
    const challengeStatus = challengeStatuses?.find(cs => cs.challenge.key === challengeKey);
    const taskProgress = challengeStatus?.progress.find(p => p.taskKey === taskKey);
    
    // If task is already completed, don't try to update it again
    if (taskProgress?.isCompleted) {
      return;
    }
    
    // Database triggers will handle automatic completion now
    // This function is mainly kept for backwards compatibility
    const shouldComplete = currentValue >= target;
    
    if (shouldComplete) {
      updateTaskProgressMutation.mutate({
        challengeKey,
        taskKey,
        isCompleted: true
      });
    }
  };

  const triggerProgressUpdate = () => {
    updateChallengeProgressMutation.mutate();
  };

  return {
    challengeStatuses: challengeStatuses || [],
    isLoading,
    calculateTaskProgress,
    triggerProgressUpdate
  };
};
